import React, { useEffect, useState } from 'react';
import { db } from './firebase';
import { getDownloadURL, ref as storageRef } from 'firebase/storage';
import { storage } from './firebase';
import { collection, doc, query, where, orderBy, onSnapshot, getDocs, getDoc, setDoc } from 'firebase/firestore';
import { addTagToThought } from './firebase';
import { toast } from 'react-hot-toast';
import ViewThoughtModal from './ViewThoughtModal';
import CreateThoughtModal from './CreateThoughtModal';
import { formatDistanceToNow } from 'date-fns';
import Sidebar from './Sidebar';
import { Card, CardHeader, CardBody, Typography, Chip } from "@material-tailwind/react";
import { useUser } from '@clerk/clerk-react';

export default function ThoughtList({ threads, setThreads, threadIndex, setShowCreateModal, thread }) {
	const { user } = useUser();
	const [thoughts, setThoughts] = useState([]);
	const [allTags, setAllTags] = useState([]);
	const [favoriteTags, setFavoriteTags] = useState([]);
	const [draggedOverThoughtId, setDraggedOverThoughtId] = useState(null);
	const [selectedThought, setSelectedThought] = useState(null);
	const [userInfo, setUserInfo] = useState({ name: '', photoURL: '', totalSize: 0, thoughtCount: 0 });

	const updateThread = (updatedProps) => {
		const updatedThreads = [...threads];
		updatedThreads[threadIndex] = { ...updatedThreads[threadIndex], ...updatedProps };
		setThreads(updatedThreads);
	};

	useEffect(() => {
		if (!user) return;
		const fetchUserStats = async () => {
			try {
				const thoughtsSnapshot = await getDocs(query(collection(db, 'thoughts'), where('userId', '==', user.id)));
				let totalSize = 0;
				thoughtsSnapshot.forEach((doc) => {
					const data = doc.data();
					totalSize += (data.imageSize || 0) + (data.thumbnailSize || 0) + (data.originalFileSize || 0);
				});
				setUserInfo({
					name: user.fullName || 'User',
					photoURL: user.imageUrl || '',
					totalSize,
					thoughtCount: thoughtsSnapshot.size,
				});
			} catch (error) {
				console.error("Error fetching user stats:", error);
				toast.error("Failed to load user statistics.");
			}
		};
		fetchUserStats();
	}, [user]);

	useEffect(() => {
		if (!user) return;
		try {
			let q = query(collection(db, 'thoughts'), where('userId', '==', user.id));

			const filterTags = thread.selectedTags || [];
			// Ensure sidebarMatchMode from thread is prioritized, fallback to filterMatchMode if needed
			const matchMode = thread.sidebarMatchMode || thread.filterMatchMode || 'any';

			if (filterTags.length > 0) {
				if (matchMode === 'any') {
					q = query(q, where('tags', 'array-contains-any', filterTags));
				} else { // 'all'
					// Firestore limit: A maximum of 10 'array-contains' or 'array-contains-any' clauses.
					// For 'all' mode, each tag is an 'array-contains' clause.
					if (filterTags.length <= 10) {
						filterTags.forEach(tag => {
							q = query(q, where('tags', 'array-contains', tag));
						});
					} else {
						console.warn("Attempting to filter by more than 10 tags in 'all' mode. This may exceed Firestore limits and result in an error or incomplete results.");
						toast.warn("Too many tags for 'Match All'. Try 'Match Any' or fewer tags.");
						// Potentially set thoughts to empty or show an error state
						setThoughts([]); // Clear thoughts to indicate an issue with the query
						return; // Exit early to prevent a potentially failing query
					}
				}
			}

			q = query(q, orderBy('timestamp', 'desc'));

			const unsubscribe = onSnapshot(q, (snapshot) => {
				setThoughts(snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() })));
			}, (error) => {
				console.error("Error fetching thoughts:", error);
				toast.error("Failed to load thoughts.");
			});
			return () => unsubscribe();
		} catch (e) {
			console.warn("Error setting up thought listener:", e);
			setThoughts([]);
		}
	}, [user, thread.selectedTags, thread.sidebarMatchMode, thread.filterMatchMode]);


	useEffect(() => {
		if (!user) return;
		try {
			const unsubscribe = onSnapshot(collection(db, 'tags'), (snapshot) => {
				setAllTags(snapshot.docs.map((doc) => doc.id).sort());
			}, (error) => {
				console.error("Error fetching all tags: ", error);
				toast.error("Failed to load tags.");
			});
			return () => unsubscribe();
		} catch (e) {
			console.warn("Error setting up all-tags listener:", e);
			setAllTags([]);
		}
	}, [user]);

	useEffect(() => {
		if (!user) return;
		const loadFavorites = async () => {
			try {
				const favRef = doc(db, 'favorites', user.id);
				const favSnap = await getDoc(favRef);
				if (favSnap.exists()) {
					setFavoriteTags(favSnap.data().tags || []);
				} else {
					setFavoriteTags([]); // Ensure it's an empty array if no doc exists
				}
			} catch (error) {
				console.error("Error loading favorite tags: ", error);
				toast.error("Failed to load favorite tags.");
			}
		};
		loadFavorites();
	}, [user]);


	const toggleFavorite = async (tag) => {
		const userId = user?.id;
		if (!userId) return;
		const favRef = doc(db, 'favorites', userId);
		const updatedFavorites = favoriteTags.includes(tag) ? favoriteTags.filter((t) => t !== tag) : [...favoriteTags, tag];
		try {
			await setDoc(favRef, { tags: updatedFavorites }, { merge: true });
			setFavoriteTags(updatedFavorites);
			// Update the favoriteTags in the current thread object as well
			updateThread({ favoriteTags: updatedFavorites });
		} catch (error) {
			console.error("Error updating favorite tags: ", error);
			toast.error("Failed to update favorite tags.");
		}
	};

	// handleGroupSelection is not directly used in ThoughtList anymore for filtering,
	// as Sidebar now updates thread.selectedTags and thread.sidebarMatchMode directly.
	// It could be kept if there's another place it's intended for, or removed if redundant.
	// const handleGroupSelection = (tags, matchAll, groupName = '') => {
	// 	updateThread({
	// 		selectedTags: tags,
	// 		sidebarMatchMode: matchAll ? 'all' : 'any',
	// 		selectedGroupName: groupName,
	//      filterSource: 'group' // Ensure filterSource is set
	// 	});
	// };
 
	const handleTagDrop = async (e, thought) => {
		e.preventDefault(); // Prevent default to allow drop
		const rawTag = e.dataTransfer.getData('text/plain');
		if (!rawTag || !user?.id) return;

		const normalizedTag = rawTag.toLowerCase().trim();
		if (!normalizedTag) return;

		const existingTags = thought.tags || [];

		if (existingTags.includes(normalizedTag)) {
			toast(`️Tag "${normalizedTag}" already exists on this thought.`, { duration: 3000, position: 'bottom-center' });
			setDraggedOverThoughtId(null); // Reset drag over state
			return;
		}

		try {
			await addTagToThought(thought.id, normalizedTag);
			toast.success(`Tag "${normalizedTag}" added to thought.`, { duration: 3000, position: 'bottom-center' });
			// Optionally, update the local thought state if not relying solely on Firestore listener
			// This can provide faster UI feedback
			const updatedThoughts = thoughts.map(t =>
				t.id === thought.id ? { ...t, tags: [...existingTags, normalizedTag] } : t
			);
			setThoughts(updatedThoughts);
		} catch (err) {
			console.error("Failed to add tag to thought:", err);
			toast.error('Failed to add tag.');
		}
		setDraggedOverThoughtId(null); // Reset drag over state
	};

	return (
		<div className="flex flex-col h-screen overflow-hidden">
			<div className="flex flex-1 flex-col sm:flex-row sm:overflow-hidden overflow-y-auto">

				{threadIndex === 0 && (
					<div className="w-[300px] min-w-[250px] sm:w-[300px] md:w-[320px] shrink-0 hidden sm:block border-r border-blue-gray-100">
						<Sidebar
							threads={threads} // Pass all threads
							updateThread={(idx, updatedThreadData) => { // Sidebar should update a specific thread
								const newThreads = [...threads];
								newThreads[idx] = { ...newThreads[idx], ...updatedThreadData };
								setThreads(newThreads);
							}}
							userInfo={userInfo}
							allTags={allTags} // Pass allTags fetched here
							favoriteTags={favoriteTags} // Pass favoriteTags fetched here
							toggleFavorite={toggleFavorite} // Pass toggleFavorite for consistency
							setShowCreateModal={setShowCreateModal} // Prop for the main create modal
						/>
					</div>
				)}

				<div className="flex-1 p-4 sm:overflow-y-auto sm:h-full">
				  <div className="w-full max-w-xl mx-auto flex flex-col gap-6"> {/* Adjusted max-width and mx-auto */}
				    <div className="mb-4">
				      <h2 className="text-2xl font-semibold text-gray-800">
				        {thread.selectedGroupName ? thread.selectedGroupName : 'Your Thoughts'}
				      </h2>
				      <p className="text-sm text-gray-600">
				        {thread.selectedGroupName
				          ? `Thoughts matching the group: "${thread.selectedGroupName}"`
				          : (thread.selectedTags && thread.selectedTags.length > 0
				            ? 'Filtered thoughts based on your selected tags.'
				            : 'A collection of all your saved thoughts.')
				          }
				      </p>
				    </div>
				    {thoughts.map((thought) => (
						<Card
							key={thought.id}
							className="cursor-pointer w-full border border-blue-gray-100 shadow-sm"
							onClick={() => setSelectedThought(thought)}
							onDragOver={(e) => {
								e.preventDefault();
								e.dataTransfer.dropEffect = "copy";
								setDraggedOverThoughtId(thought.id);
							}}
							onDragLeave={() => setDraggedOverThoughtId(null)}
							onDrop={(e) => handleTagDrop(e, thought)}
						>
							{thought.thumbnailPath && ( // Prioritize thumbnail for list view
								<CardHeader shadow={false} floated={false} className="p-0"> 
									<ThoughtImageLoader
										imagePath={thought.imagePath} // Pass full image for modal potentially
										thumbnailPath={thought.thumbnailPath}
									/>
								</CardHeader>
							)}
							<CardBody className={thought.thumbnailPath ? "pt-3" : "pt-6"}> {/* Adjust padding if no image */}
								{thought.text && (
									<Typography variant="paragraph" color="blue-gray" className="mb-2 whitespace-pre-wrap text-base">
										{thought.text}
									</Typography>
								)}
								<div className="mt-4 flex justify-end items-center">
									<Typography color="gray" className="text-xs italic text-right">
										{thought.timestamp ? `${formatDistanceToNow(thought.timestamp.toDate(), { addSuffix: true })}` : ''}
									</Typography>
								</div>
								{thought.tags?.length > 0 && (
									<div className="flex flex-wrap gap-2 mt-3">
										{thought.tags.map((tag, i) => (
											<Chip
												key={`${tag}-${i}`} // More robust key
												value={tag}
												size="sm"
												variant="ghost"
												color="blue"
												className="cursor-pointer hover:bg-blue-50"
											/>
										))}
									</div>
								)}
							</CardBody>
						</Card>
				      ))}
					  {thoughts.length === 0 && (
						<div className="text-center py-10">
							<Typography color="gray" className="text-lg">
								No thoughts match your current filters.
							</Typography>
							<Typography color="gray" className="text-sm mt-1">
								Try adjusting your selected tags or group.
							</Typography>
						</div>
					  )}
				  </div>
				</div>
			</div>
			{selectedThought && <ViewThoughtModal thought={selectedThought} user={user} onClose={() => setSelectedThought(null)} />}
		</div>
	);
}

const ThoughtImageLoader = React.memo(function ThoughtImageLoader({ imagePath, thumbnailPath }) {
	// console.log(`ThoughtImageLoader rendering for: ${thumbnailPath || imagePath}`);
	const [url, setUrl] = useState(null);
	const [error, setError] = useState(false);

	useEffect(() => {
		// console.log(`ThoughtImageLoader useEffect running for: ${thumbnailPath || imagePath}`);
		let isMounted = true;
		setError(false); // Reset error state on path change
		setUrl(null); // Reset URL to show loading or placeholder

		const fetchImage = async () => {
			// console.log(`ThoughtImageLoader fetchImage start for: ${thumbnailPath || imagePath}`);
			const selectedPath = thumbnailPath || imagePath;
			if (!selectedPath) {
				if (isMounted) {
					// console.log(`ThoughtImageLoader no selectedPath for: ${thumbnailPath || imagePath}`);
					setError(true);
				}
				return;
			}

			try {
				const ref = storageRef(storage, selectedPath);
				const downloadURL = await getDownloadURL(ref);
				if (isMounted) {
					// console.log(`ThoughtImageLoader fetchImage success, setting URL for: ${selectedPath}`);
					setUrl(downloadURL);
				}
			} catch (err) {
				console.error('Error loading image in ThoughtImageLoader:', selectedPath, err);
				if (isMounted) {
					// console.log(`ThoughtImageLoader fetchImage error for: ${selectedPath}`);
					setError(true);
				}
			}
		};
		fetchImage();
		return () => {
			// console.log(`ThoughtImageLoader useEffect cleanup for: ${thumbnailPath || imagePath}`);
			isMounted = false;
		};
	}, [imagePath, thumbnailPath]);

	if (error) {
		return <div className="w-full h-full flex items-center justify-center bg-gray-200 text-gray-500 text-xs">Image unavailable</div>;
	}
	if (!url) {
		// Optional: Placeholder or loading spinner
		return <div className="w-full h-full bg-gray-100 animate-pulse"></div>;
	}

	return <img src={url} alt="Thought" className="w-full h-full object-cover" />; // Changed to object-cover
});
